import type { ReactNode } from "react";

import { tryFormatDate, tryFormatDateTooltip } from "./formatDate";
import { useResolveI18n } from "./resolveI18n";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/v2/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/v2/tooltip";

export type DataTableColumn = {
  label: string;
};

export type CellValue = string | number | boolean | null | undefined;

export type DataTableProps = {
  columns: readonly DataTableColumn[];
  rows: readonly (readonly CellValue[])[];
};

export function DataTable({ columns, rows }: DataTableProps) {
  const resolve = useResolveI18n();

  return (
    <Table aria-label="Data table">
      <TableHeader className="sticky top-0 z-10">
        <TableRow>
          {columns.map((col, i) => (
            <TableHead key={i}>{resolve(col.label)}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row, r) => (
          <TableRow
            key={r}
            className="animate-in fade-in fill-mode-both"
            style={{ animationDelay: `${r * 40}ms` }}
          >
            {row.map((cell, c) => (
              <TableCell key={c}>{renderCell(cell, resolve)}</TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function renderCell(
  value: CellValue,
  resolve: (v: string) => string,
): ReactNode {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return value.toLocaleString("en-US");

  const str = String(value);
  const relative = tryFormatDate(str);
  if (relative) {
    const full = tryFormatDateTooltip(str);
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-default">{relative}</span>
        </TooltipTrigger>
        <TooltipContent>{full}</TooltipContent>
      </Tooltip>
    );
  }

  return resolve(str);
}
